/* ============================================================
   KELOLA MARKETPLACE  (/admin/marketplace)  —  v3.9
   ============================================================
   Pasangan rupa v3.9 bagi AdminMarketplaceView.jsx. Route memilih
   antara kedua-duanya ikut suis `config/ui_v39` (lihat App.jsx).
   Halaman lama TIDAK dipadam.

   ⛔ LOGIK DIPINDAH BULAT-BULAT, BUKAN DITULIS SEMULA.
      Halaman ini mencipta produk, menetapkan harga, menyerahkan
      kredential kepada pembeli, dan mengkreditkan komisen ejen.
      Setiap fungsi — `buildPayload`, `handleSave`, `handleDelete`,
      `handleDeliver`, `handleReject` — dipindah tanpa diubah,
      termasuk semakan, mesej dan susunan panggilannya.

   ⚠️ Harga: borang mengambil RM (perpuluhan), Firestore menyimpan
      SEN (integer). Penukaran berlaku dalam `buildPayload` dan
      `openEdit`. Jangan sentuh dua tempat itu.

   ⚠️ Kadar komisen: borang mengambil PERATUS, Firestore menyimpan
      PECAHAN (40% → 0.4). Sama, jangan sentuh.

   ⚠️ Halaman ini TIADA versi prototaip -v3.9 — ia hidup sahaja.

   Nota rupa: label kekal SAMA seperti halaman lama. Admin yang
   sudah biasa dengan halaman ini tidak patut terpaksa mencari
   semula di mana butang yang dia guna setiap hari.
   ============================================================ */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, ExternalLink, ImageOff, Boxes, Trash2 } from 'lucide-react';

import '../design/tokens.css';
import { Badge, Btn, Card, EmptyState, PageHeader, Segmented } from '../design/ui';
import { useT } from '../i18n';
import { auth } from '../firebase';
import { getUserRole, ROLES } from '../utils/roles';
import { toast } from './toastBus';
import {
    CATEGORIES, STATUSES, FULFILLMENTS, FULFILLMENT_LABELS, categoryLabel, formatPrice,
    adminListProducts, adminCreateProduct, adminUpdateProduct,
    adminReplaceImage, uploadEbook, adminDeleteProduct,
    adminListOrders, adminRejectOrder, markOrderDelivered, barisPesanan,
    adminListStockItems, adminAddStockItems, adminDeleteStockItem, parseStockBlocks,
} from '../services/marketplaceService';

const EMPTY_FORM = {
    title: '', description: '', category: 'ebook', fulfillment: 'auto_download',
    pricingMode: 'paid', priceRM: '', originalPriceRM: '', stock: '', status: 'draft',
    affiliateEnabled: false, affiliatePct: '', order: '', internalNotes: '',
    durationDays: '',
};

function defaultFulfillment(category) {
    return CATEGORIES.find((c) => c.id === category)?.defaultFulfillment || 'manual';
}

function tarikhPendek(ts) {
    const d = ts?.toDate?.() || (ts ? new Date(ts) : null);
    if (!d || Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric' });
}

/* Warna status datang daripada nada Badge, bukan kod warna sendiri —
   maksudnya sama seperti halaman lama: hijau terbit, merah habis,
   kelabu arkib, kuning draf/menunggu. */
function tonStatus(status) {
    if (status === 'published' || status === 'delivered') return 'ok';
    if (status === 'sold_out' || status === 'rejected') return 'stop';
    if (status === 'archived' || status === 'refunded') return 'neutral';
    return 'warn'; // draft · requested
}

/* Textarea sepadan `.s-input`, cuma tingginya bebas. `.s-input`
   mengunci tinggi pada 36px, jadi ia tidak boleh dipakai terus. */
const TEKS_AREA = {
    width: '100%', padding: '9px 11px', borderRadius: 8,
    background: 'var(--s-ground)', border: '1px solid var(--s-line-strong)',
    color: 'var(--s-ink)', fontFamily: 'inherit', fontSize: 13.5,
    outline: 'none', resize: 'vertical', boxSizing: 'border-box',
};

/* Medan berlabel. Label di atas, kawalan di bawah — bentuk sama
   seperti borang tetapan v3.9. */
function Medan({ label, children, span2 = false }) {
    return (
        <label style={{ display: 'block', minWidth: 0, gridColumn: span2 ? '1 / -1' : undefined }}>
            <span className="s-lbl">{label}</span>
            {children}
        </label>
    );
}

/* Pemilih fail: input asli disorok, butang menggantikannya. Input
   asli tidak boleh digayakan dan nampak terputus pada tema gelap.
   ⚠️ `ref` KEKAL pada input asli — `handleSave` membaca fail terus
      daripada ref itu, sama seperti halaman lama. Nama fail di sini
      semata-mata paparan; ia tidak masuk mana-mana simpanan. */
function PilihFail({ inputRef, accept, nama, onNama, teksKosong }) {
    const t = useT();
    return (
        <>
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                style={{ display: 'none' }}
                onChange={(e) => onNama(e.target.files?.[0]?.name || '')}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Btn variant="ghost" size="sm" onClick={() => inputRef.current?.click()}>
                    {nama ? t('adminmarket.file.change') : t('adminmarket.file.choose')}
                </Btn>
                <span className="s-hint" style={{
                    margin: 0, minWidth: 0, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260,
                }}>
                    {nama || teksKosong}
                </span>
            </div>
        </>
    );
}

/* ─── PANEL STOK AKAUN ───────────────────────────────────────
   Untuk produk `auto_stock` sahaja. Setiap baris di sini ialah satu akaun
   SEBENAR yang akan diserahkan kepada SEORANG pembeli — bukan sekadar nombor.

   Bilangan stok produk diselaraskan oleh service setiap kali senarai ini
   berubah, jadi kedai tidak boleh menjual lebih daripada akaun yang ada.

   ⛔ Akaun yang sudah terjual tidak boleh dipadam. Pembeli masih membacanya
      melalui butang "Lihat Akses" — padam bermakna dia hilang akses yang
      sudah dibayar. */
function StokModal({ product, onClose }) {
    const t = useT();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [teks, setTeks] = useState('');
    const [busy, setBusy] = useState(false);
    const berubahRef = useRef(false);

    const blok = parseStockBlocks(teks);
    const tersedia = items.filter((i) => i.status !== 'sold').length;
    const terjual = items.length - tersedia;

    useEffect(() => {
        muat();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product.id]);

    async function muat() {
        setLoading(true);
        try {
            setItems(await adminListStockItems(product.id));
        } catch (e) {
            toast.error(t('adminmarket.stock.loadFailed', { ralat: e?.message || e }));
        } finally {
            setLoading(false);
        }
    }

    async function tambah() {
        if (!blok.length) return;
        setBusy(true);
        try {
            const n = await adminAddStockItems(product.id, blok);
            toast.success(t('adminmarket.stock.added', { n }));
            setTeks('');
            berubahRef.current = true;
            await muat();
        } catch (e) {
            toast.error(t('adminmarket.stock.addFailed', { ralat: e?.message || e }));
        } finally {
            setBusy(false);
        }
    }

    async function padam(item) {
        if (!window.confirm(t('adminmarket.stock.confirmDelete'))) return;
        try {
            await adminDeleteStockItem(product.id, item.id);
            berubahRef.current = true;
            await muat();
        } catch (e) {
            toast.error(e?.message || String(e));
        }
    }

    return (
        <div className="s-root s-modal" role="dialog" aria-modal="true" aria-label={t('adminmarket.stock.aria')}
            onClick={() => !busy && onClose(berubahRef.current)}>
            <div className="s-modal__panel" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
                <header className="s-modal__head">
                    <h2 style={{ flex: 1, margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--s-ink)' }}>
                        {t('adminmarket.stock.title', { tajuk: product.title || t('adminmarket.noTitle') })}
                    </h2>
                    <button type="button" className="s-modal__x" aria-label={t('adminmarket.close')}
                        onClick={() => !busy && onClose(berubahRef.current)}>
                        <X size={15} strokeWidth={2} />
                    </button>
                </header>

                <div style={{ padding: 16, overflowY: 'auto', flex: 1, minHeight: 0 }}>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                        <Badge tone={tersedia > 0 ? 'ok' : 'stop'}>{t('adminmarket.stock.available', { n: tersedia })}</Badge>
                        <Badge>{t('adminmarket.stock.sold', { n: terjual })}</Badge>
                    </div>

                    {/* Tampal akaun */}
                    <div style={{
                        border: '1px solid var(--s-line)', borderRadius: 'var(--s-r)',
                        padding: 12, background: 'var(--s-fill)', marginBottom: 18,
                    }}>
                        <span className="s-lbl">{t('adminmarket.stock.pasteLabel')}</span>
                        <textarea
                            style={{ ...TEKS_AREA, fontFamily: 'var(--s-mono)', fontSize: 12.5 }}
                            rows={7}
                            placeholder={t('adminmarket.stock.pastePlaceholder')}
                            value={teks}
                            onChange={(e) => setTeks(e.target.value)}
                        />
                        <p className="s-hint" style={{ marginTop: 6 }}>
                            {t('adminmarket.stock.hint1')}<b>{t('adminmarket.stock.hintBlankLine')}</b>{t('adminmarket.stock.hint2')}<code>---</code>.
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                            <Btn size="sm" disabled={busy || !blok.length} onClick={tambah}>
                                {busy ? t('adminmarket.saving') : t('adminmarket.stock.addN', { n: blok.length || '' })}
                            </Btn>
                            {/* Pratonton kiraan — admin nampak pecahan blok SEBELUM menyimpan,
                                supaya tampalan yang tersalah pecah dapat dibetulkan dahulu. */}
                            {blok.length > 0 && (
                                <span className="s-hint" style={{ margin: 0 }}>
                                    {t('adminmarket.stock.detected', { senarai: blok.map((b) => b.split('\n')[0].slice(0, 22)).join(' · ') })}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Senarai */}
                    {loading ? (
                        <div className="s-hint">{t('adminmarket.stock.loading')}</div>
                    ) : items.length === 0 ? (
                        <div className="s-hint">{t('adminmarket.stock.empty')}</div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {items.map((i) => {
                                const sold = i.status === 'sold';
                                return (
                                    <div key={i.id} style={{
                                        display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px',
                                        border: '1px solid var(--s-line)', borderRadius: 'var(--s-r)',
                                        background: 'var(--s-ground)',
                                    }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                fontFamily: 'var(--s-mono)', fontSize: 12.5, color: 'var(--s-ink)',
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                                {i.label || t('adminmarket.stock.labelEmpty')}
                                            </div>
                                            {sold && (
                                                <div className="s-hint" style={{ marginTop: 2 }}>
                                                    {i.buyerEmail || t('adminmarket.stock.buyerFallback')}
                                                    {i.accessExpiresAt
                                                        ? t('adminmarket.stock.until', { tarikh: tarikhPendek(i.accessExpiresAt) })
                                                        : t('adminmarket.stock.forever')}
                                                </div>
                                            )}
                                        </div>
                                        <Badge tone={sold ? 'neutral' : 'ok'}>
                                            {sold ? t('adminmarket.stock.badgeSold') : t('adminmarket.stock.badgeAvailable')}
                                        </Badge>
                                        {!sold && (
                                            <button type="button" aria-label={t('adminmarket.stock.deleteAria')}
                                                onClick={() => padam(i)}
                                                style={{
                                                    border: 'none', background: 'transparent', cursor: 'pointer',
                                                    color: 'var(--s-ink-3)', display: 'flex', padding: 4,
                                                }}>
                                                <Trash2 size={14} strokeWidth={1.9} />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <footer className="s-modal__foot" style={{ justifyContent: 'flex-end' }}>
                    <Btn variant="ghost" onClick={() => onClose(berubahRef.current)}>{t('adminmarket.close')}</Btn>
                </footer>
            </div>
        </div>
    );
}

export default function AdminMarketplaceV39View({ initialTab = 'products' }) {
    const t = useT();
    const role = getUserRole(auth.currentUser?.email);
    const isAdmin = role === ROLES.ADMIN || role === ROLES.MODERATOR;

    /* Tab datang daripada ALAMAT (`/admin/marketplace/{tab}`) — lihat nota
       penuh dalam AdminView.jsx. Keadaan tempatan hanya cerminan alamat. */
    const navigate = useNavigate();
    const [tab, setTab] = useState(initialTab); // 'products' | 'orders'
    useEffect(() => { setTab(initialTab); }, [initialTab]);
    /* ⛔ Tukar tab = tukar ALAMAT, bukan setTab terus — kalau tidak skrin
       bertukar tanpa alamat bertukar, dan bookmark membawa ke tab salah. */
    const bukaTab = (id) => navigate(`/admin/marketplace/${id}`);

    // ─── Products ───
    const [products, setProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const imageRef = useRef(null);
    const ebookRef = useRef(null);
    const [imageName, setImageName] = useState('');
    const [ebookName, setEbookName] = useState('');
    const [stockFor, setStockFor] = useState(null); // produk yang panel stoknya dibuka

    // ─── Orders ───
    const [orderStatus, setOrderStatus] = useState('requested');
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [deliveryText, setDeliveryText] = useState({}); // orderId -> text
    const [busyOrderId, setBusyOrderId] = useState(null);

    // ═══ LOAD ═══
    async function loadProducts() {
        setLoadingProducts(true);
        try {
            setProducts(await adminListProducts());
        } catch (e) {
            toast.error(t('adminmarket.products.loadFailed', { ralat: e?.message || e }));
        } finally {
            setLoadingProducts(false);
        }
    }

    async function loadOrders(status = orderStatus) {
        setLoadingOrders(true);
        try {
            setOrders(await adminListOrders(status));
        } catch (e) {
            toast.error(t('adminmarket.orders.loadFailed', { ralat: e?.message || e }));
        } finally {
            setLoadingOrders(false);
        }
    }

    useEffect(() => {
        if (isAdmin) loadProducts();
        /* `loadProducts` dicipta semula setiap render (ia kini memanggil
           t() untuk mesej ralat), jadi memasukkannya ke dalam senarai
           kebergantungan bermakna senarai produk dimuat semula tanpa
           henti. Corak sama seperti useEffect di bawah. */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);
    useEffect(() => {
        if (isAdmin && tab === 'orders') loadOrders(orderStatus);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, orderStatus, isAdmin]);

    if (!isAdmin) {
        return (
            <>
                <div style={{ padding: 40, color: 'var(--s-stop)' }}>{t('adminmarket.accessDenied')}</div>
            </>
        );
    }

    // ═══ FORM ═══
    function openCreate() {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setImageName('');
        setEbookName('');
        setShowForm(true);
    }

    function openEdit(p) {
        setEditingId(p.id);
        setForm({
            title: p.title || '',
            description: p.description || '',
            category: p.category || 'ebook',
            fulfillment: p.fulfillment || defaultFulfillment(p.category),
            pricingMode: p.pricingMode || 'paid',
            priceRM: p.price ? (p.price / 100).toFixed(2) : '',
            originalPriceRM: p.originalPrice ? (p.originalPrice / 100).toFixed(2) : '',
            stock: p.stock == null ? '' : String(p.stock),
            status: p.status || 'draft',
            affiliateEnabled: !!p.affiliateEnabled,
            affiliatePct: p.affiliateRate == null ? '' : String(Math.round(p.affiliateRate * 100)),
            order: p.order == null ? '' : String(p.order),
            internalNotes: '',
            durationDays: p.durationDays == null ? '' : String(p.durationDays),
        });
        setImageName('');
        setEbookName('');
        setShowForm(true);
    }

    function setField(k, v) {
        setForm((f) => {
            const next = { ...f, [k]: v };
            if (k === 'category') next.fulfillment = defaultFulfillment(v);
            return next;
        });
    }

    function buildPayload() {
        const isFree = form.pricingMode === 'free';
        const priceSen = isFree ? 0 : Math.round(parseFloat(form.priceRM || '0') * 100);
        const origSen = Math.round(parseFloat(form.originalPriceRM || '0') * 100);
        const pct = form.affiliatePct === '' ? null : parseFloat(form.affiliatePct);
        return {
            title: form.title,
            description: form.description,
            category: form.category,
            fulfillment: form.fulfillment,
            pricingMode: form.pricingMode,
            price: Number.isFinite(priceSen) ? priceSen : 0,
            originalPrice: Number.isFinite(origSen) ? origSen : 0,
            stock: form.stock,
            status: form.status,
            affiliateEnabled: form.affiliateEnabled,
            affiliateRate: pct == null || Number.isNaN(pct) ? null : pct / 100, // simpan sebagai PECAHAN
            order: form.order,
            internalNotes: form.internalNotes,
            durationDays: form.durationDays,
        };
    }

    async function handleSave() {
        if (!form.title.trim()) { toast.error(t('adminmarket.form.needTitle')); return; }
        if (form.affiliatePct !== '' && (parseFloat(form.affiliatePct) <= 0 || parseFloat(form.affiliatePct) > 100)) {
            toast.error(t('adminmarket.form.rateRange')); return;
        }
        setSaving(true);
        try {
            const payload = buildPayload();
            // Stok produk auto-serah dikira daripada senarai akaun, bukan ditaip.
            // Panel Stok satu-satunya penulis nombor itu — kalau borang turut
            // menghantarnya, kiraan sebenar akan ditimpa nilai lama pada skrin.
            if (payload.fulfillment === 'auto_stock') {
                if (editingId) delete payload.stock;
                else payload.stock = 0;
            }
            const imageFile = imageRef.current?.files?.[0] || null;
            const ebookFile = ebookRef.current?.files?.[0] || null;
            if (ebookFile && ebookFile.type !== 'application/pdf') {
                toast.error(t('adminmarket.form.ebookMustPdf')); setSaving(false); return;
            }

            if (editingId) {
                await adminUpdateProduct(editingId, payload);
                if (imageFile) await adminReplaceImage(editingId, imageFile);
                if (ebookFile) await uploadEbook(editingId, ebookFile, payload.internalNotes);
                toast.success(t('adminmarket.form.updated'));
            } else {
                await adminCreateProduct(payload, imageFile, ebookFile);
                toast.success(t('adminmarket.form.created'));
            }
            setShowForm(false);
            setForm(EMPTY_FORM);
            setEditingId(null);
            await loadProducts();
        } catch (e) {
            toast.error(t('adminmarket.form.saveFailed', { ralat: e?.message || e }));
        } finally {
            setSaving(false);
        }
    }

    async function handleDelete(p) {
        if (!window.confirm(t('adminmarket.products.confirmDelete', { tajuk: p.title }))) return;
        try {
            await adminDeleteProduct(p);
            toast.success(t('adminmarket.products.deleted'));
            setProducts((arr) => arr.filter((x) => x.id !== p.id));
        } catch (e) {
            toast.error(t('adminmarket.products.deleteFailed', { ralat: e?.message || e }));
        }
    }

    // ═══ ORDERS ═══
    async function handleDeliver(order) {
        const info = (deliveryText[order.id] || '').trim();
        // Pesanan troli membawa banyak barang — keputusan dibuat daripada BARIS,
        // bukan medan aras-atas yang hanya wujud pada pesanan satu barang.
        // Produk auto-serah tidak perlu apa-apa daripada admin (akaun datang
        // daripada stok) dan ebook pun tidak (failnya sudah ada); kotak teks itu
        // nota dalaman sahaja. Hanya barang serah-tangan yang menuntut teks.
        const barisOrder = barisPesanan(order);
        const perluInfo = barisOrder.some((b) => b.category !== 'ebook' && b.fulfillment !== 'auto_stock');
        if (perluInfo && !info) {
            toast.error(t('adminmarket.orders.needDeliveryInfo'));
            return;
        }
        setBusyOrderId(order.id);
        try {
            const res = await markOrderDelivered(order.id, info);
            const asas = res?.autoStock
                ? (res.isRenewal ? t('adminmarket.orders.accessExtended') : t('adminmarket.orders.accountHandedOver'))
                : t('adminmarket.orders.markedDelivered');
            if (res?.commissionCredited) {
                toast.success(t('adminmarket.orders.commission', { asas, jumlah: formatPrice(res.commissionAmountSen) }));
            } else {
                toast.success(asas);
            }
            if (res?.autoStock) loadProducts(); // kiraan stok berubah
            setOrders((arr) => arr.filter((x) => x.id !== order.id));
        } catch (e) {
            toast.error(t('adminmarket.orders.deliverFailed', { ralat: e?.message || e }));
        } finally {
            setBusyOrderId(null);
        }
    }

    async function handleReject(order) {
        const reason = window.prompt(t('adminmarket.orders.rejectPrompt'), '') ?? '';
        setBusyOrderId(order.id);
        try {
            await adminRejectOrder(order.id, reason);
            toast.success(t('adminmarket.orders.rejected'));
            setOrders((arr) => arr.filter((x) => x.id !== order.id));
        } catch (e) {
            toast.error(t('adminmarket.orders.rejectFailed', { ralat: e?.message || e }));
        } finally {
            setBusyOrderId(null);
        }
    }

    // ═══ RENDER ═══
    return (
        <>
            {/* Penuh lebar — itu bentuk lalai v3.9 untuk halaman grid dan
                senarai (Products, Sales, Notes, Marketplace semuanya begitu).
                Had lebar disimpan HANYA untuk halaman bacaan seperti Changelog
                dan Help. Halaman ini grid, jadi ia mengisi panel. */}
            <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px' }}>

                <PageHeader
                    title={t('adminmarket.title')}
                    subtitle={t('adminmarket.subtitle')}
                    actions={tab === 'products' ? (
                        <Btn icon={<Plus size={15} strokeWidth={2} />} onClick={openCreate}>{t('adminmarket.addProduct')}</Btn>
                    ) : undefined}
                />

                <div style={{ marginBottom: 18 }}>
                    <Segmented
                        value={tab}
                        onChange={bukaTab}
                        options={[
                            { value: 'products', label: t('adminmarket.tab.products'), href: '/admin/marketplace/products' },
                            { value: 'orders', label: t('adminmarket.tab.orders'), href: '/admin/marketplace/orders' },
                        ]}
                    />
                </div>

                {/* ─── PRODUK ─── */}
                {tab === 'products' && (
                    loadingProducts ? (
                        <div className="s-pgrid">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="s-skel" style={{
                                    aspectRatio: '4 / 4.4', borderRadius: 'var(--s-r-lg)',
                                    border: '1px solid var(--s-line)', background: 'var(--s-card)',
                                }} />
                            ))}
                        </div>
                    ) : products.length === 0 ? (
                        <EmptyState
                            title={t('adminmarket.products.emptyTitle')}
                            action={<Btn icon={<Plus size={15} strokeWidth={2} />} onClick={openCreate}>{t('adminmarket.addProduct')}</Btn>}
                        >
                            {t('adminmarket.products.emptyDesc')}
                        </EmptyState>
                    ) : (
                        <div className="s-pgrid">
                            {products.map((p) => (
                                <Card key={p.id} className="s-pcard" style={{ overflow: 'hidden' }}>
                                    <div className="s-thumb" style={{
                                        height: 120, borderBottom: '1px solid var(--s-line)',
                                        background: p.imageUrl
                                            ? `var(--s-ground) center/cover no-repeat url(${JSON.stringify(p.imageUrl)})`
                                            : 'var(--s-ground)',
                                    }}>
                                        {!p.imageUrl && <ImageOff size={20} strokeWidth={1.5} />}
                                        <span style={{ position: 'absolute', top: 8, right: 8 }}>
                                            <Badge tone={tonStatus(p.status)}>{p.status}</Badge>
                                        </span>
                                    </div>

                                    <div style={{ padding: 12, display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--s-ink)', lineHeight: 1.35 }}>
                                            {p.title || t('adminmarket.noTitle')}
                                        </div>
                                        <div className="s-hint" style={{ marginTop: 3 }}>
                                            {categoryLabel(p.category)} · {p.pricingMode === 'free' ? t('adminmarket.free') : formatPrice(p.price)}
                                        </div>

                                        <div style={{ display: 'flex', gap: 6, marginTop: 9, flexWrap: 'wrap' }}>
                                            <Badge>
                                                {p.stock == null
                                                    ? t('adminmarket.card.stockUnlimited')
                                                    : t('adminmarket.card.stockN', { n: p.stock })}
                                            </Badge>
                                            {p.fulfillment === 'auto_stock' && (
                                                <Badge tone="ok">
                                                    {p.durationDays
                                                        ? t('adminmarket.card.autoDeliverDays', { n: p.durationDays })
                                                        : t('adminmarket.card.autoDeliver')}
                                                </Badge>
                                            )}
                                            {p.affiliateEnabled && (
                                                <Badge tone="ok">
                                                    {p.affiliateRate != null
                                                        ? t('adminmarket.card.affiliatePct', { peratus: Math.round(p.affiliateRate * 100) })
                                                        : t('adminmarket.card.affiliateGlobal')}
                                                </Badge>
                                            )}
                                        </div>

                                        <div style={{ marginTop: 'auto', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {p.fulfillment === 'auto_stock' && (
                                                <Btn variant="ghost" size="sm"
                                                    icon={<Boxes size={14} strokeWidth={1.9} />}
                                                    onClick={() => setStockFor(p)}>
                                                    {t('adminmarket.card.manageStock')}
                                                </Btn>
                                            )}
                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <Btn variant="ghost" size="sm" style={{ flex: 1 }} onClick={() => openEdit(p)}>{t('adminmarket.card.edit')}</Btn>
                                                <Btn variant="ghost" size="sm" style={{ flex: 1, color: 'var(--s-stop)' }}
                                                    onClick={() => handleDelete(p)}>{t('adminmarket.card.delete')}</Btn>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )
                )}

                {/* ─── ORDER ─── */}
                {tab === 'orders' && (
                    <>
                        <div style={{ marginBottom: 14 }}>
                            <Segmented
                                value={orderStatus}
                                onChange={setOrderStatus}
                                options={[
                                    { value: 'requested', label: t('adminmarket.orders.filter.requested') },
                                    // Pesanan troli yang sebahagian barangnya kehabisan stok.
                                    // Ia ADA barisan sendiri kerana duit sudah masuk dan
                                    // pelanggan sedang menunggu — ia tidak boleh bercampur
                                    // dengan pesanan siap dan hilang daripada pandangan.
                                    { value: 'partial', label: t('adminmarket.orders.filter.partial') },
                                    { value: 'delivered', label: t('adminmarket.orders.filter.delivered') },
                                    { value: 'rejected', label: t('adminmarket.orders.filter.rejected') },
                                ]}
                            />
                        </div>

                        {loadingOrders ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {[1, 2].map((i) => (
                                    <div key={i} className="s-skel" style={{
                                        height: 120, borderRadius: 'var(--s-r-lg)',
                                        border: '1px solid var(--s-line)', background: 'var(--s-card)',
                                    }} />
                                ))}
                            </div>
                        ) : orders.length === 0 ? (
                            <EmptyState title={t('adminmarket.orders.emptyTitle')}>
                                {t('adminmarket.orders.emptyDesc')}
                            </EmptyState>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {orders.map((o) => (
                                    <Card key={o.id} style={{ padding: 14 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--s-ink)', lineHeight: 1.35 }}>
                                                    {o.productTitle}
                                                </div>
                                                <div className="s-hint" style={{ marginTop: 3 }}>
                                                    {categoryLabel(barisPesanan(o)[0]?.category)} · {formatPrice(o.amount)} · {o.buyerEmail}
                                                </div>
                                                {/* Senarai barang untuk pesanan troli. Admin perlu
                                                    nampak BERAPA unit setiap barang sebelum menekan
                                                    Serah — kalau tidak dia tidak tahu berapa akaun
                                                    yang perlu ada dalam stok. */}
                                                {(() => {
                                                    const bs = barisPesanan(o);
                                                    if (bs.length <= 1 && !bs.some((b) => b.qty > 1)) return null;
                                                    return (
                                                        <ul style={{ margin: '6px 0 0', padding: 0, listStyle: 'none' }}>
                                                            {bs.map((b) => (
                                                                <li key={b.idx} className="s-hint">
                                                                    {b.qty} × {b.productTitle}
                                                                    {b.shortfall > 0 && ` — ${t('adminmarket.orders.shortfall', { n: b.shortfall })}`}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    );
                                                })()}
                                                {o.buyerWhatsapp && (
                                                    <div className="s-hint">
                                                        {t('adminmarket.orders.whatsapp', { nombor: o.buyerWhatsapp })}
                                                    </div>
                                                )}
                                                {o.affiliateRef && (
                                                    <div className="s-hint">
                                                        {t('adminmarket.orders.ref', { kod: o.affiliateRef })}
                                                    </div>
                                                )}
                                                {o.proofImageUrl && (
                                                    <a href={o.proofImageUrl} target="_blank" rel="noreferrer" style={{
                                                        display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 6,
                                                        fontSize: 12.5, color: 'var(--s-ink-2)', textDecoration: 'underline',
                                                    }}>
                                                        <ExternalLink size={12} strokeWidth={2} />{t('adminmarket.orders.viewProof')}
                                                    </a>
                                                )}
                                            </div>
                                            <Badge tone={tonStatus(o.status)} style={{ height: 'fit-content' }}>{o.status}</Badge>
                                        </div>

                                        {(orderStatus === 'requested' || orderStatus === 'partial') && (
                                            <div style={{ marginTop: 10 }}>
                                                <textarea
                                                    style={TEKS_AREA}
                                                    rows={barisPesanan(o).some((b) => b.category !== 'ebook' && b.fulfillment !== 'auto_stock') ? 4 : 2}
                                                    placeholder={barisPesanan(o).some((b) => b.fulfillment === 'auto_stock')
                                                        ? t('adminmarket.orders.notePlaceholderStock')
                                                        : barisPesanan(o).every((b) => b.category === 'ebook')
                                                            ? t('adminmarket.orders.notePlaceholderEbook')
                                                            : t('adminmarket.orders.deliveryPlaceholder')}
                                                    value={deliveryText[o.id] || ''}
                                                    onChange={(e) => setDeliveryText((d) => ({ ...d, [o.id]: e.target.value }))}
                                                />
                                                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                                                    <Btn size="sm" disabled={busyOrderId === o.id} onClick={() => handleDeliver(o)}>
                                                        {busyOrderId === o.id
                                                            ? t('adminmarket.orders.processing')
                                                            : t('adminmarket.orders.markDelivered')}
                                                    </Btn>
                                                    <Btn variant="ghost" size="sm" disabled={busyOrderId === o.id}
                                                        onClick={() => handleReject(o)}>{t('adminmarket.orders.reject')}</Btn>
                                                </div>
                                            </div>
                                        )}
                                    </Card>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ─── BORANG PRODUK ─── */}
            {showForm && (
                <div
                    className="s-root s-modal"
                    role="dialog" aria-modal="true"
                    aria-label={editingId ? t('adminmarket.form.editProduct') : t('adminmarket.addProduct')}
                    onClick={() => !saving && setShowForm(false)}
                >
                    <div className="s-modal__panel" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
                        <header className="s-modal__head">
                            <h2 style={{ flex: 1, margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--s-ink)' }}>
                                {editingId ? t('adminmarket.form.editProduct') : t('adminmarket.addProduct')}
                            </h2>
                            <button type="button" className="s-modal__x" aria-label={t('adminmarket.close')}
                                onClick={() => !saving && setShowForm(false)}>
                                <X size={15} strokeWidth={2} />
                            </button>
                        </header>

                        <div style={{
                            padding: 16, overflowY: 'auto', flex: 1, minHeight: 0,
                            display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14,
                        }}>
                            <Medan label={t('adminmarket.form.title')} span2>
                                <input className="s-input" value={form.title}
                                    onChange={(e) => setField('title', e.target.value)} />
                            </Medan>

                            <Medan label={t('adminmarket.form.description')} span2>
                                <textarea style={TEKS_AREA} rows={3} value={form.description}
                                    onChange={(e) => setField('description', e.target.value)} />
                            </Medan>

                            <Medan label={t('adminmarket.form.category')}>
                                <select className="s-input" value={form.category}
                                    onChange={(e) => setField('category', e.target.value)}>
                                    {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                                </select>
                            </Medan>
                            <Medan label={t('adminmarket.form.fulfillment')}>
                                <select className="s-input" value={form.fulfillment}
                                    onChange={(e) => setField('fulfillment', e.target.value)}>
                                    {FULFILLMENTS.map((f) => (
                                        <option key={f} value={f}>{FULFILLMENT_LABELS[f] || f}</option>
                                    ))}
                                </select>
                            </Medan>

                            <Medan label={t('adminmarket.form.pricingMode')}>
                                <select className="s-input" value={form.pricingMode}
                                    onChange={(e) => setField('pricingMode', e.target.value)}>
                                    <option value="paid">{t('adminmarket.form.paid')}</option>
                                    <option value="free">{t('adminmarket.free')}</option>
                                </select>
                            </Medan>
                            <Medan label={t('adminmarket.form.status')}>
                                <select className="s-input" value={form.status}
                                    onChange={(e) => setField('status', e.target.value)}>
                                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </Medan>

                            {form.pricingMode !== 'free' && (
                                <>
                                    <Medan label={t('adminmarket.form.price')}>
                                        <input className="s-input" type="number" step="0.01" value={form.priceRM}
                                            onChange={(e) => setField('priceRM', e.target.value)} />
                                    </Medan>
                                    <Medan label={t('adminmarket.form.originalPrice')}>
                                        <input className="s-input" type="number" step="0.01" value={form.originalPriceRM}
                                            onChange={(e) => setField('originalPriceRM', e.target.value)} />
                                    </Medan>
                                </>
                            )}

                            {form.fulfillment === 'auto_stock' ? (
                                <Medan label={t('adminmarket.form.stock')}>
                                    <input className="s-input"
                                        value={editingId
                                            ? t('adminmarket.form.stockAccounts', { n: form.stock || 0 })
                                            : t('adminmarket.form.stockNone')}
                                        readOnly disabled style={{ opacity: 0.7 }} />
                                    <p className="s-hint" style={{ marginTop: 5 }}>
                                        {t('adminmarket.form.stockAutoHint1')}<b>{t('adminmarket.card.manageStock')}</b>{t('adminmarket.form.stockAutoHint2')}
                                    </p>
                                </Medan>
                            ) : (
                                <Medan label={t('adminmarket.form.stockManual')}>
                                    <input className="s-input" type="number" value={form.stock}
                                        onChange={(e) => setField('stock', e.target.value)} />
                                </Medan>
                            )}
                            <Medan label={t('adminmarket.form.order')}>
                                <input className="s-input" type="number" value={form.order}
                                    onChange={(e) => setField('order', e.target.value)} />
                            </Medan>

                            {form.fulfillment === 'auto_stock' && (
                                <Medan label={t('adminmarket.form.duration')} span2>
                                    <input className="s-input" type="number" min="1" placeholder={t('adminmarket.form.durationPlaceholder')}
                                        value={form.durationDays}
                                        onChange={(e) => setField('durationDays', e.target.value)} />
                                    <p className="s-hint" style={{ marginTop: 5 }}>
                                        {t('adminmarket.form.durationHint1')}<b>30</b>{t('adminmarket.form.durationHint2')}
                                        <b>{t('adminmarket.form.durationHintBlank')}</b>{t('adminmarket.form.durationHint3')}
                                    </p>
                                </Medan>
                            )}

                            {/* Affiliate */}
                            <div style={{
                                gridColumn: '1 / -1', border: '1px solid var(--s-line)',
                                borderRadius: 'var(--s-r)', padding: 12, background: 'var(--s-fill)',
                                display: 'flex', flexDirection: 'column', gap: 12,
                            }}>
                                <label style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    fontSize: 13, color: 'var(--s-ink)', cursor: 'pointer',
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={form.affiliateEnabled}
                                        onChange={(e) => setField('affiliateEnabled', e.target.checked)}
                                        style={{ accentColor: 'var(--s-select)', width: 15, height: 15 }}
                                    />
                                    {t('adminmarket.form.affiliateEnable')}
                                </label>
                                {form.affiliateEnabled && (
                                    <Medan label={t('adminmarket.form.affiliateRate')}>
                                        <input className="s-input" type="number" step="1" min="1" max="100"
                                            placeholder={t('adminmarket.form.affiliateRatePlaceholder')}
                                            value={form.affiliatePct} onChange={(e) => setField('affiliatePct', e.target.value)} />
                                    </Medan>
                                )}
                            </div>

                            <Medan label={`${t('adminmarket.form.image')}${editingId ? t('adminmarket.form.keepBlank') : ''}`} span2>
                                <PilihFail
                                    inputRef={imageRef} accept="image/*"
                                    nama={imageName} onNama={setImageName}
                                    teksKosong={t('adminmarket.file.none')}
                                />
                            </Medan>

                            {form.category === 'ebook' && (
                                <Medan label={`${t('adminmarket.form.ebook')}${editingId ? t('adminmarket.form.keepBlank') : ''}`} span2>
                                    <PilihFail
                                        inputRef={ebookRef} accept="application/pdf"
                                        nama={ebookName} onNama={setEbookName}
                                        teksKosong={t('adminmarket.file.none')}
                                    />
                                </Medan>
                            )}

                            <Medan label={t('adminmarket.form.internalNotes')} span2>
                                <textarea style={TEKS_AREA} rows={2} value={form.internalNotes}
                                    onChange={(e) => setField('internalNotes', e.target.value)} />
                            </Medan>
                        </div>

                        <footer className="s-modal__foot" style={{ justifyContent: 'flex-end' }}>
                            <Btn variant="ghost" onClick={() => setShowForm(false)} disabled={saving}>{t('adminmarket.cancel')}</Btn>
                            <Btn onClick={handleSave} disabled={saving}>{saving ? t('adminmarket.saving') : t('adminmarket.save')}</Btn>
                        </footer>
                    </div>
                </div>
            )}

            {/* ─── PANEL STOK AKAUN ─── */}
            {stockFor && (
                <StokModal
                    product={stockFor}
                    onClose={(berubah) => {
                        setStockFor(null);
                        if (berubah) loadProducts(); // kiraan stok + status produk mungkin bertukar
                    }}
                />
            )}
        </>
    );
}
